import React, { useState, useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { GameMode, Language } from '../types';

interface SquareMagicGameProps {
  mode: GameMode;
  onReset: () => void;
  language: Language;
}

const isPerfectSquare = (n: number) => Number.isInteger(Math.sqrt(n));

const getRandomSquare = () => {
  const base = Math.floor(Math.random() * 10) + 1; // 1^2 to 10^2
  return base * base;
};

const getRandomNonSquare = () => {
  let n;
  do {
    n = Math.floor(Math.random() * 100) + 1;
  } while (isPerfectSquare(n));
  return n;
};

const generateNewChallenge = () => {
  const correct = getRandomSquare();
  const distractors = [getRandomNonSquare(), getRandomNonSquare()];
  const options = [correct, ...distractors].sort(() => Math.random() - 0.5);
  return { correct, options };
};

const SquareMagicGame: React.FC<SquareMagicGameProps> = ({ onReset, language }) => {
  const [round, setRound] = useState(1);
  const [correctAnswer, setCorrectAnswer] = useState<number | null>(null);
  const [options, setOptions] = useState<number[]>([]);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [score, setScore] = useState(0);

  useEffect(() => {
    const { correct, options } = generateNewChallenge();
    setCorrectAnswer(correct);
    setOptions(options);
    setFeedback(null);
  }, [round]);

  const handleClick = (choice: number) => {
    if (choice === correctAnswer) {
      setScore(prev => prev + 1);
      setFeedback(language === 'en' ? "✨ Magic! That's right!" : '✨ Magique ! Bonne réponse !');
    } else {
      setFeedback(
        language === 'en'
          ? `Oops! The correct answer was ${correctAnswer}`
          : `Oups ! La bonne réponse était ${correctAnswer}`
      );
    }
    setTimeout(() => setRound(prev => prev + 1), 2000);
  };

  return (
    <div className="w-full max-w-4xl">
      <div className="mb-6 flex items-center justify-between">
        <button
          onClick={onReset}
          className="flex items-center text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 transition-colors"
        >
          <ArrowLeft className="h-5 w-5 mr-1" />
          {language === 'en' ? 'Back to Modes' : 'Retour aux modes'}
        </button>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-6 border border-slate-200 dark:border-slate-700">
        <div className="text-center mb-8">
          <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-4">
            {language === 'en' 
              ? 'Find the perfect square number'
              : 'Trouvez le nombre carré parfait'}
          </h2>
          <p className="text-slate-600 dark:text-slate-300">
            {language === 'en'
              ? 'Which of these numbers is a perfect square?'
              : 'Lequel de ces nombres est un carré parfait ?'}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          {options.map((num, idx) => (
            <button
              key={idx}
              onClick={() => handleClick(num)}
              className="aspect-square rounded-lg text-3xl font-bold transition-all
                bg-slate-50 dark:bg-slate-700 text-slate-700 dark:text-slate-200 
                hover:bg-indigo-50 dark:hover:bg-indigo-900/50 hover:text-indigo-600 dark:hover:text-indigo-300"
            >
              {num}
            </button>
          ))}
        </div>

        {feedback && (
          <div className="text-center text-lg font-medium text-indigo-600 dark:text-indigo-400 mb-4">
            {feedback}
          </div>
        )}

        <div className="text-center text-xl font-semibold text-indigo-600 dark:text-indigo-400">
          {language === 'en' ? 'Score: ' : 'Score : '}{score}
        </div>
      </div>
    </div>
  );
};

export default SquareMagicGame;